/**
 * /privacy — Privacy Policy.
 * Operator: Thoughtplanet Advisory Services LLP.
 *
 * Drafted to be substantively compliant with India's Digital Personal
 * Data Protection Act 2023 (DPDP), the EU/UK GDPR, and California's CCPA.
 * Not a substitute for legal review — content reflects how the Service
 * actually behaves as of the effective date.
 */
import { LegalShell } from "@/components/LegalShell";

export function PrivacyPage() {
  return (
    <LegalShell title="Privacy Policy" effectiveDate="June 2, 2026">
      <p>
        This Privacy Policy describes how{" "}
        <strong>Thoughtplanet Advisory Services LLP</strong>{" "}
        (&ldquo;Curvle,&rdquo; &ldquo;we,&rdquo; &ldquo;us&rdquo;)
        collects, uses, shares, and protects information about you when
        you use the Curvle daily free-kick puzzle game and related
        services (the &ldquo;Service&rdquo;).
      </p>

      <h2>1. The Data We Collect</h2>

      <h3>1.1 Information you provide directly</h3>
      <ul>
        <li>
          <strong>Email address</strong> — when you sign in via magic-link
          or Google OAuth, or when you purchase Curvle Pro.
        </li>
        <li>
          <strong>Display name</strong> — if you set one for the
          leaderboard. Optional.
        </li>
        <li>
          <strong>Profile picture &amp; locale</strong> — only if you sign
          in with Google, in which case we receive what Google&rsquo;s
          OAuth scope returns.
        </li>
        <li>
          <strong>Country override</strong> — if you manually pick a
          country for the country-locked friend leagues.
        </li>
        <li>
          <strong>Payment information</strong> — handled directly by our
          payment providers (see Section 4). Curvle never sees or stores
          your card details.
        </li>
      </ul>

      <h3>1.2 Information collected automatically</h3>
      <ul>
        <li>
          <strong>Anonymous device identifier</strong> — a randomly
          generated string stored in an HttpOnly cookie
          (<code>curvle_cid</code>) that lets you keep a streak across
          sessions before you sign in.
        </li>
        <li>
          <strong>Country code</strong> — derived from edge proxy headers
          (Cloudflare / Vercel / Railway) the first time we see your
          request. Used for the country-locked friend leagues. You can
          override this manually.
        </li>
        <li>
          <strong>Gameplay data</strong> — for each daily attempt: the
          three input parameters (angle, power, curl), the resulting
          outcome, and a sampled trajectory used to render the replay.
        </li>
        <li>
          <strong>Streak &amp; longest-streak counters</strong>.
        </li>
        <li>
          <strong>Server logs</strong> — IP address, user agent, request
          path, response status, and timestamp. Used for security, abuse
          prevention, and debugging. Logs are retained for up to 30 days.
        </li>
        <li>
          <strong>Push subscription</strong> — if you grant permission for
          web push notifications.
        </li>
      </ul>

      <h3>1.3 Cookies</h3>
      <ul>
        <li>
          <code>curvle_cid</code> — anonymous device id, HttpOnly,
          1-year max age.
        </li>
        <li>
          <code>curvle_at</code> — short-lived access token (~15 min),
          HttpOnly, set after sign-in.
        </li>
        <li>
          <code>curvle_rt</code> — refresh token (~30 days), HttpOnly,
          path-scoped to <code>/api/auth</code>.
        </li>
        <li>
          <code>curvle_oauth_state</code> — short-lived CSRF token used
          during the Google sign-in handshake.
        </li>
      </ul>
      <p>
        Curvle does not use third-party advertising or tracking cookies.
      </p>

      <h2>2. How We Use Your Data</h2>
      <ul>
        <li>To operate the Service (render the daily puzzle, save your
          attempt, compute the leaderboard, deliver web push if enabled).</li>
        <li>To authenticate you across devices.</li>
        <li>To process and deliver Curvle Pro, including recording your
          purchase and handling any refund requests.</li>
        <li>To prevent fraud, abuse, and automated cheating
          (rate-limiting, attempt locks, bot detection).</li>
        <li>To send transactional email — sign-in magic links, receipts,
          and important account or service notices.</li>
        <li>To respond to support requests.</li>
      </ul>
      <p>
        We do not use your data to train machine-learning models, sell it
        to third parties, or display advertising.
      </p>

      <h2>3. Legal Bases (EU / UK / India)</h2>
      <p>
        Where the EU/UK GDPR applies, we rely on the following legal bases:
      </p>
      <ul>
        <li><strong>Performance of a contract</strong> — to deliver
          gameplay and Pro features.</li>
        <li><strong>Legitimate interests</strong> — to secure the Service,
          prevent abuse, and operate the leaderboard.</li>
        <li><strong>Consent</strong> — for web push notifications and any
          optional features you explicitly enable.</li>
        <li><strong>Legal obligation</strong> — to retain transaction
          records required by tax law.</li>
      </ul>
      <p>
        Where India&rsquo;s Digital Personal Data Protection Act 2023
        applies, we process personal data on the basis of your consent
        (which you may withdraw) and for the &ldquo;certain legitimate
        uses&rdquo; permitted by the Act (notably to deliver a service you
        have requested).
      </p>

      <h2>4. Sharing &amp; Sub-Processors</h2>
      <p>
        We share personal data only with the providers that operate parts
        of the Service on our behalf:
      </p>
      <ul>
        <li><strong>Railway</strong> — application hosting (US).</li>
        <li><strong>PostgreSQL on Railway</strong> — primary database.</li>
        <li><strong>Upstash</strong> — Redis-as-a-service for caching,
          rate limits, and idempotency.</li>
        <li><strong>Resend</strong> — transactional email (sign-in links,
          receipts).</li>
        <li><strong>Google LLC</strong> — when you choose Google sign-in;
          we receive only the OAuth profile fields (id, email, name,
          picture, locale).</li>
        <li><strong>Cloudflare / Vercel / Railway edge</strong> — country
          detection from IP headers.</li>
        <li><strong>Payment provider</strong> — Gumroad, Inc.,
          our current Merchant of Record. Gumroad processes card details
          and is the seller of record for the transaction. Gumroad has its
          own privacy policy. We may engage additional payment providers in
          future; this list is updated when that happens.</li>
      </ul>
      <p>
        We do not sell or rent personal data. We may disclose data if
        required by law or to protect the rights, property, or safety of
        Curvle, our users, or the public.
      </p>

      <h2>5. International Transfers</h2>
      <p>
        Curvle is operated from India and uses sub-processors located in
        the United States and Europe. Where personal data is transferred
        out of your country, we rely on appropriate safeguards including
        Standard Contractual Clauses (where applicable) and the
        sub-processor&rsquo;s own compliance commitments.
      </p>

      <h2>6. Data Retention</h2>
      <ul>
        <li>Anonymous gameplay data (linked to <code>curvle_cid</code>)
          is retained for the lifetime of the cookie or until you delete
          your account.</li>
        <li>Account data (email, display name, country, attempts,
          streaks) is retained while your account is active and for up to
          30 days after deletion to allow recovery, after which it is
          permanently removed.</li>
        <li>Purchase &amp; payment records are retained as required
          by applicable tax and accounting laws (typically 7 years in
          India).</li>
        <li>Server logs are retained for up to 30 days.</li>
        <li>Magic-link tokens are stored as SHA-256 hashes and expire
          within 15 minutes of issuance.</li>
        <li>Refresh tokens are stored as SHA-256 hashes; revoked tokens
          are pruned after 90 days.</li>
      </ul>

      <h2>7. Your Rights</h2>
      <p>
        Subject to applicable law, you have the right to:
      </p>
      <ul>
        <li>Access the personal data we hold about you.</li>
        <li>Correct inaccurate data.</li>
        <li>Delete your account and associated data. You can do this at
          any time from the in-app account settings; the deletion is
          immediate and cascades to your attempts, refresh tokens, and
          magic-link tokens.</li>
        <li>Withdraw consent for processing that relies on consent (e.g.
          push notifications) without affecting the lawfulness of prior
          processing.</li>
        <li>Object to processing based on our legitimate interests.</li>
        <li>Receive a portable copy of your data.</li>
        <li>Lodge a complaint with the data-protection authority in your
          jurisdiction.</li>
      </ul>
      <p>
        To exercise any of these rights, email{" "}
        <a href="mailto:vbraju@thoughtplanet.com">vbraju@thoughtplanet.com</a>.
        We will respond within 30 days (or such shorter period as required
        by applicable law).
      </p>

      <h2>8. Children</h2>
      <p>
        Curvle is not directed at children under 13. We do not knowingly
        collect personal data from children under 13. If you believe we
        have inadvertently collected such data, please contact us and we
        will delete it.
      </p>

      <h2>9. Security</h2>
      <p>
        We use industry-standard measures to protect personal data,
        including TLS encryption in transit, hashed password-equivalent
        tokens at rest, HttpOnly + Secure cookies, fail-closed rate
        limiting on authentication endpoints, authenticated, server-side-verified payment-provider webhooks, and regular dependency audits. No
        method of transmission or storage is 100% secure; we cannot
        guarantee absolute security.
      </p>

      <h2>10. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. Material
        changes will be communicated via email or in-app notice. The
        effective date at the top of this page indicates when the current
        version took effect.
      </p>

      <h2>11. Contact</h2>
      <p>
        Privacy questions or rights requests:{" "}
        <a href="mailto:vbraju@thoughtplanet.com">vbraju@thoughtplanet.com</a>
      </p>
    </LegalShell>
  );
}
